import { useTranslation } from 'react-i18next';
import { Icon } from '@/common/components/Icons/Icon';
import { PLAN, TPlan } from '@/common/constants/markets';
import { ROUTES } from '@/common/constants/routes';
import { useFeatures } from '@/common/integrations/feature-flag/feature-flag.hooks';
import { useBudgetsSummary } from '@/common/integrations/menhir/employee/budgets/budgets.hooks';
import { useGetDiscounts } from '@/common/integrations/menhir/employee/discounts/discounts.hooks';
import { useGetFeatures } from '@/common/integrations/menhir/employee/features/features.hooks';
import { useGetInsurancePolicies } from '@/common/integrations/menhir/employee/insurance/insurance.hooks';
import { useGetPockets } from '@/common/integrations/menhir/employee/pockets/pockets.hooks';
import { useGetEWADetails } from '@/common/integrations/menhir/employee/salary/salary.hooks';
import { useGetSpotlights } from '@/common/integrations/menhir/employee/spotlights/spotlights.hooks';
import { useBenefits } from '@/web/hooks/useBenefits';
import { TabsWrapper } from './TabsWrapper/TabsWrapper';

export const PTTabsLayout = ({ plan }: { plan: TPlan }) => {
  const { t } = useTranslation();
  const { benefitProducts, mealProducts } = useBenefits();
  const { budgetsAvailable } = useBudgetsSummary();
  const {
    features: { ff_employee_coverflex_ai },
  } = useFeatures();
  const {
    features: { FF_BUDGET_WITH_APPLICATION },
  } = useGetFeatures();
  const {
    mealPocket,
    benefitsPocket,
    isLoading: isLoadingPockets,
  } = useGetPockets();
  const { policies, upcomingPolicy } = useGetInsurancePolicies();
  const getEWADetails = useGetEWADetails();
  const { discounts } = useGetDiscounts();
  const { spotlights } = useGetSpotlights();

  if (plan === PLAN.SUC_MEAL) {
    return (
      <TabsWrapper
        items={[
          {
            href: ROUTES.ROOT,
            label: t('navigation.home'),
            icon: <Icon icon="home" />,
            exact: true,
          },
          {
            href: ROUTES.ACTIVITY,
            label: t('navigation.activity'),
            icon: <Icon icon="right-left" />,
          },
          {
            href: ROUTES.DISCOUNTS,
            label: t('navigation.discounts'),
            icon: <Icon icon="tags" />,
            hidden: discounts?.length === 0,
            sidebar: true,
          },
          {
            href: ROUTES.PROFILE,
            label: t('navigation.profile'),
            icon: <Icon icon="user" />,
            sidebar: true,
          },
        ]}
      />
    );
  }

  return (
    <TabsWrapper
      loading={isLoadingPockets}
      items={[
        {
          href: ROUTES.ROOT,
          label: t('navigation.wallet'),
          icon: <Icon icon="home" />,
          hidden: !mealPocket && !benefitsPocket,
          exact: true,
        },
        {
          href: ROUTES.MEAL,
          label: t('navigation.meal_card'),
          icon: <Icon icon="utensils" />,
          hidden: !mealProducts?.length || !mealPocket,
        },
        {
          href: ROUTES.BENEFITS,
          label: t('navigation.benefits'),
          icon: <Icon icon="seedling" />,
          hidden: !benefitsPocket || !benefitProducts?.length,
        },
        {
          href: ROUTES.BUDGETS,
          label: t('navigation.budgets'),
          icon: <Icon icon="star" />,
          hidden: !budgetsAvailable && !FF_BUDGET_WITH_APPLICATION,
        },
        {
          href: ROUTES.EWA,
          label: t('navigation.ewa'),
          icon: <Icon icon="money-bill-trend-up" />,
          hidden: !getEWADetails.details,
          sidebar: true,
        },
        {
          href: ROUTES.INSURANCE,
          label: t('navigation.insurance'),
          icon: <Icon icon="shield-plus" />,
          hidden: !policies?.length && !upcomingPolicy,
          notification: spotlights?.insurance?.upgradePeriod?.active,
          sidebar: true,
        },
        {
          href: ROUTES.DISCOUNTS,
          label: t('navigation.discounts'),
          icon: <Icon icon="tags" />,
          hidden: discounts?.length === 0,
          sidebar: true,
        },
        {
          href: ROUTES.COVERFLEX_AI,
          label: t('navigation.coverflex_ai'),
          icon: <Icon icon="sparkles" />,
          hidden: !ff_employee_coverflex_ai,
        },
        {
          href: ROUTES.PROFILE,
          label: t('navigation.profile'),
          icon: <Icon icon="user" />,
          sidebar: true,
        },
      ]}
    />
  );
};
